import { LoadingSpinner } from '@momentum-ui/react-collaboration';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { VoicemailAudioProvider } from '../components/Voicemail/VoicemailAudioProvider';
import { VoicemailList } from '../components/Voicemail/VoicemailList';
import { useMakeCall } from '../hooks/useMakeCall';
import useVoicemails from '../hooks/useVoicemails';
import useWebexClasses from '../hooks/useWebexClasses';
import { voicemailLabel } from '../utils/MetricUtils';
import { getVoicemailPollingInterval } from '../utils/voicemailPollingConfig';
import { EmptyVoiceMails } from './EmptyVoiceMails';
import { MobileVoicemail } from './MobileVoicemail';
import { VoicemailNotConfigured } from './VoicemailNotConfigured';
import './WebexVoicemail.styles.scss';

export interface IWebexVoicemailProps {
  updateUnreadCount?: (unread: number) => void;
  style?: React.CSSProperties;
  noVoicemailMessage?: string | undefined;
  handleSetIsVmPlayErrorMsg: (newValue: boolean) => void;
  isLocaleGerman?: boolean;
  isActiveTab?: boolean;
  testMode?: boolean;
  makeCall?: (address: string, isVideo?: boolean, label?: string) => void;
  mobileLayout?: boolean;
  userID?: string;
  onMobileDetailOpenChange?: (isOpen: boolean) => void;
  onCallVoicemail?: () => void;
}

export interface IWebexVoicemailHandle {
  refreshVoicemails: () => void;
}

/**
 * @param {string} obj.noVoicemailMessage The message when voicemails is empty
 * @param {React.CSSProperties} obj.style Custom style for overriding this component's CSS
 */

export const WebexVoicemail = forwardRef<
  IWebexVoicemailHandle,
  IWebexVoicemailProps
>(({ updateUnreadCount, style, noVoicemailMessage, handleSetIsVmPlayErrorMsg, isLocaleGerman, isActiveTab, testMode = false, makeCall: makeCallProp, mobileLayout = false, userID, onMobileDetailOpenChange, onCallVoicemail }, ref) => {
  const {
    voicemails,
    loading,
    deleteVoicemail,
    readVoicemail,
    unreadVoicemail,
    markAsRead,
    refresh,
    voicemailSrc,
    voicemailId,
    isShowVmPlayError,
    isShowVmConfigPage,
    fetchNextBatch,
    hasMoreVoicemails,
    transcripts = {},
    transcriptKinds = {},
    transcriptsLoading = {},
    getVMTranscript = () => {},
    isWebexCallingBackend = false,
  } = useVoicemails(userID);

  useImperativeHandle(ref, () => ({
    refreshVoicemails() {
      refresh();
    },
  }));

  const [selectedVoicemailId, setSelectedVoicemailId] = useState<string>();
  const { t } = useTranslation('WebexVoicemail');
  const [adapterMakeCall] = useMakeCall();
  const makeCall = makeCallProp ?? adapterMakeCall;
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [isShowVoicemails, setIsShowVoicemails] = useState<boolean>(false);
  const [expandedVoicemailId, setExpandedVoicemailId] = useState<string | null>(null);

  const handlePress = useCallback((voicemailId: string) => {
    setExpandedVoicemailId(prevIndex => (prevIndex === voicemailId ? null : voicemailId));
  }, []);

  useEffect(() => {
    const newValue = isShowVmPlayError ? true : false;
    handleSetIsVmPlayErrorMsg(newValue);
  }, [isShowVmPlayError]);

  useEffect(() => {
    if (updateUnreadCount) {
      updateUnreadCount(
        voicemails.filter((voicemail) => voicemail.unread).length
      );
    }
  }, [voicemails, updateUnreadCount]);

  useEffect(() => {
    if (isActiveTab !== true) {
      return undefined;
    }
    // Use a per-org polling interval: orgs on the fast-polling allow-list
    // (e.g. UHS, Ford) refresh every 10s; all other orgs keep the default 60s
    // cadence to avoid increasing voicemail backend load.
    const pollingIntervalMs = getVoicemailPollingInterval();
    const interval = setInterval(() => {
      refresh();
    }, pollingIntervalMs);

    return () => clearInterval(interval);
  }, [isActiveTab, refresh]);

  useEffect(() => {
    if (isShowVmConfigPage) {
      setIsShowVoicemails(false);
      setIsEmpty(false);
    } else if (voicemails?.length > 0) {
      setIsShowVoicemails(true);
      setIsEmpty(false);
    } else if (voicemails?.length === 0) {
      setIsEmpty(true);
      setIsShowVoicemails(false);
    }

  }, [voicemails, loading, refresh, isShowVmConfigPage]);
  const [cssClasses] = useWebexClasses('voicemail-widget');
  const transcriptRequestActions = useMemo(() => {
    const actions = new Map<string, () => void>();
    voicemails.forEach((voicemail) => {
      actions.set(voicemail.id, () => getVMTranscript(voicemail.id));
    });
    return actions;
  }, [getVMTranscript, voicemails]);
  const voicemailItems = useMemo(
    () =>
      voicemails.map((voicemail, index) => ({
        voicemail,
        onPress: () => {
          setSelectedVoicemailId(voicemail.id);
          handlePress(voicemail.id);
        },
        itemIndex: index,
        isSelected: expandedVoicemailId === voicemail.id,
        onDelete: () => deleteVoicemail(voicemail.id),
        onRead: () => readVoicemail(voicemail.id, voicemail.unread),
        onMarkRead: () => markAsRead(voicemail.id),
        onUnread: () => unreadVoicemail(voicemail.id),
        voicemailSrc: voicemail.id === voicemailId ? voicemailSrc : '',
        voicemailSrcLoader: voicemail.id === voicemailId,
        useMakeAudioCall: () =>
          makeCall(voicemail.address, false, voicemailLabel),
        useMakeVideoCall: () =>
          makeCall(voicemail.address, true, voicemailLabel),
        isLocaleGerman,
        transcript: transcripts[voicemail.id],
        transcriptKind: transcriptKinds[voicemail.id],
        transcriptLoading: Boolean(transcriptsLoading[voicemail.id]),
        transcriptEnabled: isWebexCallingBackend,
        onRequestTranscript: transcriptRequestActions.get(voicemail.id),
      })),
    [
      deleteVoicemail,
      unreadVoicemail,
      markAsRead,
      expandedVoicemailId,
      handlePress,
      isLocaleGerman,
      isWebexCallingBackend,
      makeCall,
      readVoicemail,
      transcriptKinds,
      transcripts,
      transcriptsLoading,
      transcriptRequestActions,
      voicemailId,
      voicemailSrc,
      voicemails,
    ]
  );

  return (
    <div className={cssClasses} style={{ ...style, height: '100%' }}>
      {loading ? (<LoadingSpinner scale={48} data-testid="loading-spinner" />)
        : (
          <div>
            {(isEmpty && !isShowVmConfigPage && !mobileLayout) && (
              <div style={{ marginTop: '8rem' }}>
                <EmptyVoiceMails
                  noVoicemailMessage={noVoicemailMessage || t('noVoicemailMessage')}
                  data-testid="no-data-found"
                />
              </div>
            )}
            {isShowVmConfigPage && (
              <div>
                <VoicemailNotConfigured
                  voicemailSetupMessage={t('voicemailSetupMessage')}
                  voicemailConactAdminMessage={t('voicemailConactAdminMessage')}
                />
              </div>
            )}
            {(!isShowVmConfigPage && mobileLayout) && (
              <VoicemailAudioProvider>
                <MobileVoicemail
                  items={voicemailItems}
                  isLocaleGerman={isLocaleGerman}
                  fetchNextBatch={fetchNextBatch}
                  hasMoreItems={hasMoreVoicemails}
                  onDetailOpenChange={onMobileDetailOpenChange}
                  onCallVoicemail={onCallVoicemail}
                />
              </VoicemailAudioProvider>
            )}
            {(isShowVoicemails && !isShowVmConfigPage && !mobileLayout) && (
              <VoicemailList
                items={voicemailItems}
                height={400}
                width={"100%"}
                // Ensure virtualized row height matches the actual 50px ListItemBase footprint
                itemHeight={50}
                isActive={isActiveTab}
                testMode={testMode}
                fetchNextBatch={fetchNextBatch}
              />
            )}
          </div>
        )}

    </div>
  );
});

WebexVoicemail.defaultProps = {
  updateUnreadCount: undefined,
  style: undefined,
  noVoicemailMessage: undefined,
};
